import type { PageParam, PageResult, PagedQueryArg } from './paged-types';
import { serializeWhere } from './query-params';
import type { QueryWhereValue } from './query-params';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ActiveVersionItem = {
    id: string;
    product: string;
    version: string;
    totalCount: number;
    latestAttempt: string;
};

export type VersionDrilldownItem = {
    id: string;
    totalCount: number;
    subscriptionName: string;
    login: string;
    latestAttempt: string;
    version: string | null;
};

type ActiveVersionApiItem = {
    id?: string;
    product?: string | null;
    version?: string | null;
    totalCount?: string | number | null;
    latestAttempt?: string | null;
};

type ActiveVersionsApiResponse = {
    signIns?: ActiveVersionApiItem[];
};

type DrilldownApiItem = {
    id?: string;
    totalCount?: string | number | null;
    subscriptionName?: string | null;
    login?: string | null;
    attemptedOn?: string | null;
    subscriptionId?: string | null;
    version?: string | null;
};

type DrilldownApiResponse = {
    signIns?: DrilldownApiItem[];
};

// ---------------------------------------------------------------------------
// Aggregate query-string builder
// ---------------------------------------------------------------------------

type AggregateSpec = { fn: string; icol: string; ocol: string };

interface AggregateQueryArgs {
    groups: string[];
    aggregates: AggregateSpec[];
    order?: Array<[field: string, dir: 'ASC' | 'DESC']>;
    where?: Record<string, QueryWhereValue>;
}

const buildAggregateQueryString = (args: AggregateQueryArgs): string => {
    const params = new URLSearchParams();

    args.groups.forEach((g) => params.append('groups[]', g));

    args.aggregates.forEach((agg, i) => {
        params.set(`aggregates[${i}][fn]`, agg.fn);
        params.set(`aggregates[${i}][icol]`, agg.icol);
        params.set(`aggregates[${i}][ocol]`, agg.ocol);
    });

    if (args.order && args.order.length > 0) {
        args.order.forEach((entry, i) => {
            params.set(`order[${i}][0]`, entry[0]);
            params.set(`order[${i}][1]`, entry[1]);
        });
    }

    if (args.where) serializeWhere(params, args.where);

    // Ensure all aggregated rows are returned
    params.set('limit', '1000');

    return params.toString();
};

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

const byLatestAttemptDesc = (
    a: { latestAttempt: string },
    b: { latestAttempt: string },
): number => {
    if (!a.latestAttempt && !b.latestAttempt) return 0;
    if (!a.latestAttempt) return 1;
    if (!b.latestAttempt) return -1;
    return b.latestAttempt.localeCompare(a.latestAttempt);
};

const toPageResult = <T extends { latestAttempt: string }>(rows: T[]): PageResult<T> => {
    // Server strips ORDER BY from aggregate queries, so we sort client-side.
    const sorted = [...rows].sort(byLatestAttemptDesc);
    return { rows: sorted, total: sorted.length, offset: 0, limit: sorted.length };
};

const buildSignInsQuery = (
    groups: readonly string[],
    aggregates: AggregateSpec[],
    queryArg: PagedQueryArg,
): { url: string; method: 'get' } => {
    const qs = buildAggregateQueryString({
        groups: [...groups],
        aggregates,
        order: queryArg.order,
        where: { ...queryArg.where },
    });
    return { url: `/mp/1/signIns?${qs}`, method: 'get' as const };
};

const INFINITE_QUERY_OPTIONS = {
    initialPageParam: { offset: 0 },
    getNextPageParam: (): PageParam | undefined => undefined,
};

// ---------------------------------------------------------------------------
// Infinite query config
// ---------------------------------------------------------------------------

const GROUPS = ['product', 'version'] as const;

const AGGREGATES: AggregateSpec[] = [
    // Use createdAt (never null) instead of version — COUNT(version) returns 0 for
    // null-version groups because SQL COUNT skips NULLs.
    { fn: 'COUNT', icol: 'createdAt', ocol: 'totalCount' },
    { fn: 'MAX', icol: 'attemptedOn', ocol: 'latestAttempt' },
];

/** Sentinel value used in URLs for sign-ins that have no version string. */
export const NO_VERSION = '_none';

const transformActiveVersion = (item: ActiveVersionApiItem): ActiveVersionItem => ({
    id: `${item.product ?? ''}-${item.version ?? NO_VERSION}`,
    product: item.product ?? '',
    version: item.version ?? '',
    totalCount: Number(item.totalCount) || 0,
    latestAttempt: item.latestAttempt ?? '',
});

export type ActiveVersionsPageResult = PageResult<ActiveVersionItem>;
export type ActiveVersionsQueryArg = PagedQueryArg;

export const activeVersionsInfiniteQuery = {
    query: ({
        queryArg,
        pageParam: _pageParam,
    }: {
        queryArg: ActiveVersionsQueryArg;
        pageParam: PageParam;
    }) => buildSignInsQuery(GROUPS, AGGREGATES, queryArg),

    transformResponse: (response: ActiveVersionsApiResponse): ActiveVersionsPageResult =>
        toPageResult((response?.signIns ?? []).map(transformActiveVersion)),

    infiniteQueryOptions: INFINITE_QUERY_OPTIONS,
};

// ---------------------------------------------------------------------------
// Drill-down query config (per-subscription/user breakdown)
// ---------------------------------------------------------------------------

const DRILLDOWN_GROUPS = ['subscriptionId', 'login', 'version', 'product'] as const;

const DRILLDOWN_AGGREGATES: AggregateSpec[] = [
    { fn: 'COUNT', icol: 'subscriptionId', ocol: 'totalCount' },
    { fn: 'MAX', icol: 'subscriptionName', ocol: 'subscriptionName' },
    { fn: 'MAX', icol: 'attemptedOn', ocol: 'attemptedOn' },
    { fn: 'MAX', icol: 'companyName', ocol: 'companyName' },
    { fn: 'MAX', icol: 'customerName', ocol: 'customerName' },
];

const transformDrilldownItem = (item: DrilldownApiItem): VersionDrilldownItem => ({
    id: item.subscriptionId ?? `${item.login ?? ''}-${item.subscriptionName ?? ''}`,
    totalCount: Number(item.totalCount) || 0,
    subscriptionName: item.subscriptionName ?? '',
    login: item.login ?? '',
    latestAttempt: item.attemptedOn ?? '',
    version: item.version ?? null,
});

export type VersionDrilldownPageResult = PageResult<VersionDrilldownItem>;
export type VersionDrilldownQueryArg = PagedQueryArg;

export const versionDrilldownInfiniteQuery = {
    query: ({
        queryArg,
        pageParam: _pageParam,
    }: {
        queryArg: VersionDrilldownQueryArg;
        pageParam: PageParam;
    }) => buildSignInsQuery(DRILLDOWN_GROUPS, DRILLDOWN_AGGREGATES, queryArg),

    transformResponse: (response: DrilldownApiResponse): VersionDrilldownPageResult =>
        toPageResult((response?.signIns ?? []).map(transformDrilldownItem)),

    infiniteQueryOptions: INFINITE_QUERY_OPTIONS,
};
